'use client';

import { useState } from 'react';
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import { TrackedButton } from '@/components/button/tracked-button';
import { customRichTextTags } from '@/utils/i18n';
import CtaBox, {
  CTA_BOX_BUTTON_CLASSNAME,
  CTA_BOX_BUTTON_TEXT_CLASSNAME,
} from './cta-box';

// 모달 코드는 버튼을 눌러 렌더될 때만 로드한다 (reanalysis footer-cta 와 동일 패턴)
const CaseReviewModal = dynamic(() => import('@/components/case-review-modal'));

// 버튼 클릭 추적 라벨 prefix (다중 삽입 시 _1, _2 로 구분)
const CASE_REVIEW_CTA_LABEL = 'blog_case_review_cta';
// 블로그 유입 케이스리뷰 문의를 재분석과 구분하기 위한 gtag generate_lead 라벨
const CASE_REVIEW_LEAD_LABEL = 'blog_case_review_inquiry';

export interface CaseReviewCtaProps {
  label: string;
  index: number;
}

/**
 * 본문 안에 들어가는 케이스리뷰 CTA. 문의 CTA 와 같은 박스를 쓰고 버튼이 여는
 * 대상만 다르다(퍼널 대신 케이스리뷰 모달).
 *
 * 문구는 지금 문의 CTA 와 같은 값이지만 네임스페이스는 따로 둔다 — 한쪽 문구를
 * 고칠 때 다른 쪽이 딸려 바뀌지 않게 하기 위해서다.
 *
 * 버튼은 문의 CTA 와 달리 높이를 예약하지 않는다 — 모달만 지연 로드하고
 * 버튼 자체는 바로 렌더돼 layout shift 가 없다.
 */
const CaseReviewCta = ({ label, index }: CaseReviewCtaProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const t = useTranslations('Blog.main.caseReviewCta');

  return (
    <CtaBox
      title={t.rich('title', customRichTextTags)}
      description={t('description')}
    >
      <TrackedButton
        type="button"
        label={`${CASE_REVIEW_CTA_LABEL}_${index + 1}`}
        position="main_cta"
        onClick={() => setIsModalOpen(true)}
        className={CTA_BOX_BUTTON_CLASSNAME}
      >
        <span className={CTA_BOX_BUTTON_TEXT_CLASSNAME}>{label}</span>
      </TrackedButton>
      {isModalOpen && (
        <CaseReviewModal
          onClose={() => setIsModalOpen(false)}
          leadLabel={CASE_REVIEW_LEAD_LABEL}
        />
      )}
    </CtaBox>
  );
};

export default CaseReviewCta;
